import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import type { BannerKind } from '@/components/top-banner'

export type BannerRow = Database['public']['Tables']['banners']['Row']

export type BannerAudience = 'everyone' | 'organizations'

/** A banner as the admin list shows it: the row plus the orgs it targets. */
export interface AdminBanner extends BannerRow {
  kind: BannerKind
  audience: BannerAudience
  org_ids: string[]
}

export interface BannerInput {
  kind: BannerKind
  title: string | null
  message: string
  link_url: string | null
  link_label: string | null
  audience: BannerAudience
  org_ids: string[]
  dismissible: boolean
  is_active: boolean
}

/**
 * What the signed-in user should see right now: an active banner for
 * everyone, or for an organization they belong to — super admin or not. Refetched
 * on focus and every minute so an admin's change reaches open tabs without a
 * reload.
 */
export const siteBanners = {
  visible: () =>
    queryOptions({
      queryKey: ['banners', 'visible'],
      queryFn: async (): Promise<AdminBanner[]> => {
        // An RPC rather than the table: RLS lets super admins read every
        // banner (for the admin list), but as users they should only see the
        // ones aimed at them.
        const { data, error } = await supabase.rpc('visible_banners')
        if (error) {
          throw new Error(`Failed to fetch banners: ${error.message}`, {
            cause: error,
          })
        }
        return (data ?? []).map((row) => ({
          ...row,
          kind: row.kind as BannerKind,
          audience: row.audience as BannerAudience,
          org_ids: [],
        }))
      },
      staleTime: 60 * 1000,
      refetchInterval: 60 * 1000,
      refetchOnWindowFocus: true,
    }),
}

// ── Super admin ────────────────────────────────────────────────────────

export const adminBannersQueryOptions = () =>
  queryOptions({
    queryKey: ['admin', 'banners'],
    queryFn: async (): Promise<AdminBanner[]> => {
      const { data, error } = await supabase
        .from('banners')
        .select('*, banner_organizations(org_id)')
        .order('is_active', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) {
        throw new Error(`Failed to fetch banners: ${error.message}`, {
          cause: error,
        })
      }
      return (data ?? []).map(({ banner_organizations, ...row }) => ({
        ...row,
        kind: row.kind as BannerKind,
        audience: row.audience as BannerAudience,
        org_ids: (banner_organizations ?? []).map((bo) => bo.org_id),
      }))
    },
    staleTime: 30 * 1000,
  })

function rowFromInput(input: BannerInput) {
  return {
    kind: input.kind,
    title: input.title?.trim() || null,
    message: input.message.trim(),
    link_url: input.link_url?.trim() || null,
    link_label: input.link_label?.trim() || null,
    audience: input.audience,
    dismissible: input.dismissible,
    is_active: input.is_active,
  }
}

async function replaceOrganizations(bannerId: string, orgIds: string[]) {
  const { error: clearError } = await supabase
    .from('banner_organizations')
    .delete()
    .eq('banner_id', bannerId)
  if (clearError) {
    throw new Error(`Failed to update banner audience: ${clearError.message}`, {
      cause: clearError,
    })
  }
  if (orgIds.length === 0) {
    return
  }
  const { error } = await supabase
    .from('banner_organizations')
    .insert(orgIds.map((org_id) => ({ banner_id: bannerId, org_id })))
  if (error) {
    throw new Error(`Failed to update banner audience: ${error.message}`, {
      cause: error,
    })
  }
}

export async function createBanner(input: BannerInput): Promise<string> {
  const { data, error } = await supabase
    .from('banners')
    .insert(rowFromInput(input))
    .select('id')
    .single()
  if (error || !data) {
    throw new Error(`Failed to create banner: ${error?.message}`, {
      cause: error,
    })
  }
  if (input.audience === 'organizations') {
    await replaceOrganizations(data.id, input.org_ids)
  }
  return data.id
}

export async function updateBanner(
  id: string,
  input: BannerInput
): Promise<void> {
  const { error } = await supabase
    .from('banners')
    .update(rowFromInput(input))
    .eq('id', id)
  if (error) {
    throw new Error(`Failed to update banner: ${error.message}`, {
      cause: error,
    })
  }
  await replaceOrganizations(
    id,
    input.audience === 'organizations' ? input.org_ids : []
  )
}

export async function setBannerActive(
  id: string,
  isActive: boolean
): Promise<void> {
  const { error } = await supabase
    .from('banners')
    .update({ is_active: isActive })
    .eq('id', id)
  if (error) {
    throw new Error(`Failed to update banner: ${error.message}`, {
      cause: error,
    })
  }
}

export async function deleteBanner(id: string): Promise<void> {
  const { error } = await supabase.from('banners').delete().eq('id', id)
  if (error) {
    throw new Error(`Failed to delete banner: ${error.message}`, {
      cause: error,
    })
  }
}
